import { useState } from 'react';
import { Box, Typography, TextField, Autocomplete, Chip } from '@mui/material';
import type { Role, Permission } from 'types';
import { createRole, updateRole } from 'services/rolesApi';
import { globalSnackbar } from 'components/GlobalSnackbar';
import DrawerShell, { DrawerError } from '../DrawerShell';
import { fieldSx } from '../shared';

interface RoleDrawerProps {
  /** Редактируемая роль; `null` — режим создания. */
  role: Role | null;
  availablePermissions: Permission[];
  onClose: () => void;
  onSaved: () => void;
}

export default function RoleDrawer({
  role,
  availablePermissions,
  onClose,
  onSaved,
}: RoleDrawerProps) {
  const isEdit = role !== null;

  const [name, setName] = useState(role?.name ?? '');
  const [title, setTitle] = useState(role?.title ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>(() =>
    role
      ? availablePermissions.filter((p) => role.permissions.some((rp) => rp.name === p.name))
      : []
  );
  const [nameError, setNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameError('Введите системное имя роли');
      return;
    }
    setSubmitting(true);
    setApiError('');
    const payload = {
      name: name.trim(),
      title: title.trim() || null,
      description: description.trim() || null,
      permissions: selectedPermissions.map((p) => ({ name: p.name, title: p.title ?? p.name })),
    };
    try {
      if (isEdit && role) {
        await updateRole(role.id, payload);
        globalSnackbar({ message: 'Роль обновлена', severity: 'success' });
      } else {
        await createRole(payload);
        globalSnackbar({ message: 'Роль создана', severity: 'success' });
      }
      onSaved();
      onClose();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Ошибка сохранения роли');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DrawerShell
      title={isEdit ? 'Редактирование роли' : 'Новая роль'}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={isEdit ? 'Сохранить' : 'Создать'}
      submittingLabel={isEdit ? 'Сохранение...' : 'Создание...'}
    >
      <DrawerError message={apiError} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Системное имя (name)"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError('');
          }}
          size="small"
          fullWidth
          error={Boolean(nameError)}
          helperText={nameError || 'Уникальный технический идентификатор, напр. lawyer'}
          sx={fieldSx}
        />
        <TextField
          label="Название (title)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          size="small"
          fullWidth
          placeholder="Юрист"
          sx={fieldSx}
        />
        <TextField
          label="Описание"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={fieldSx}
        />

        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--color-text-muted)',
              mb: 1,
            }}
          >
            Пермишены
          </Typography>
          <Autocomplete
            multiple
            options={availablePermissions}
            getOptionLabel={(option) => option.title || option.name}
            value={selectedPermissions}
            onChange={(_, newValue) => setSelectedPermissions(newValue)}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                // eslint-disable-next-line react/jsx-key
                <Chip
                  size="small"
                  label={option.title || option.name}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Выберите пермишены" size="small" sx={fieldSx} />
            )}
          />
        </Box>
      </Box>
    </DrawerShell>
  );
}
